import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Bell, LogOut, Menu, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ThemeToggle } from '@/components/ThemeToggle';
import { UserAvatar } from '@/components/UserAvatar';
import { signOut, useSession } from '@/api/auth';
import { useIsAdmin } from '@/api/hooks';
import { cn } from '@/lib/utils';
import { useNotifications } from '@/contexts/NotificationContext';
import { ROUTES, ticketPath } from '@/lib/routes';
import { Sidebar } from '@/components/shell/Sidebar';
import { ClientPicker } from '@/components/shell/ClientPicker';
import { CosmosWordmark } from '@/components/CosmosMark';
import { PageSearch } from '@/components/PageSearch';
import { RecordFind } from '@/components/shell/RecordFind';
import { BookmarkButton } from '@/components/shell/Bookmarks';
import { NewMenu } from '@/components/shell/NewMenu';

/**
 * Top header — logo, page search, notifications, theme, user. Module navigation lives in the
 * left sidebar (components/shell/Sidebar). Next to the logo is the global client picker
 * (`ClientPicker`, dev/planning/general.md §4.9 Phase B). On mobile a menu button opens the sidebar as a sheet;
 * the PSA BottomNav stays.
 */
export function AppHeader() {
  const loc = useLocation();
  const navigate = useNavigate();
  const { data: session } = useSession();
  const { newTickets, clearNotifications } = useNotifications();

  const [navOpen, setNavOpen] = useState(false);
  // The mobile sheet closes whenever the route changes (a link was followed).
  useEffect(() => { setNavOpen(false); }, [loc.pathname, loc.search]);

  const handleSignOut = async () => {
    await signOut();
    toast.success('Signed out');
    window.location.href = ROUTES.login;
  };

  const userName = (session?.user as { name?: string; email?: string } | undefined)?.name
                ?? session?.user?.email
                ?? '';
  const userEmail = session?.user?.email ?? null;
  const { data: isAdmin } = useIsAdmin();
  const isSettings = loc.pathname.startsWith(ROUTES.account.root) || loc.pathname.startsWith(ROUTES.admin.root);

  return (
    <header className="sticky top-0 z-30 border-b border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
      <div className="flex h-14 w-full items-center justify-between gap-4 px-4">
        {/* Left: mobile menu button + logo. Navigation itself is the sidebar (components/shell/Sidebar). */}
        <div className="flex min-w-0 items-center gap-2">
          <Button variant="ghost" size="icon" className="md:hidden" aria-label="Open navigation" onClick={() => setNavOpen(true)}>
            <Menu className="h-5 w-5"/>
          </Button>
          <Link to={ROUTES.home} aria-label="Cosmos home" className="shrink-0">
            <CosmosWordmark size={28} />
          </Link>
          {/* Global client scope (CIPP/Glass put theirs here too). */}
          <div className="ml-4 hidden md:block"><ClientPicker /></div>
        </div>

        {/* Right: utilities — page search first (desktop only) */}
        <div className="flex items-center gap-2">
          <BookmarkButton />
          <RecordFind />
          <PageSearch />
          <NewMenu />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div className="relative hidden sm:inline-flex">
                <Button variant="ghost" size="icon" aria-label="Notifications">
                  <Bell className="h-4 w-4"/>
                </Button>
                {newTickets.length > 0 && (
                  <span className="pointer-events-none absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-0.5 text-[10px] font-bold leading-none text-white">
                    {newTickets.length > 9 ? '9+' : newTickets.length}
                  </span>
                )}
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <div className="flex items-center justify-between border-b border-divider px-3 py-2">
                <span className="text-xs font-semibold text-foreground">New tickets this session</span>
                {newTickets.length > 0 && (
                  <button
                    onClick={clearNotifications}
                    className="text-[11px] text-muted-foreground hover:text-foreground"
                  >
                    Clear all
                  </button>
                )}
              </div>
              {newTickets.length === 0 ? (
                <div className="px-3 py-5 text-center text-xs text-muted-foreground">No new tickets yet</div>
              ) : (
                <>
                  <div className="max-h-64 overflow-y-auto">
                    {newTickets.map((t) => (
                      <DropdownMenuItem
                        key={t.id}
                        onSelect={() => { navigate(ticketPath(t.id)); }}
                        className="flex flex-col items-start gap-0.5 px-3 py-2"
                      >
                        <span className="text-[11px] font-medium text-muted-foreground">{t.ticketNumber}</span>
                        <span className="line-clamp-1 text-xs text-foreground">{t.title}</span>
                      </DropdownMenuItem>
                    ))}
                  </div>
                  <div className="border-t border-divider px-3 py-2">
                    <button
                      onClick={() => { navigate(ROUTES.psa.tickets.queues); clearNotifications(); }}
                      className="text-[11px] text-brand-strong hover:underline dark:text-brand"
                    >
                      View all in Queues →
                    </button>
                  </div>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <ThemeToggle/>

          {/* Settings: personal settings for everyone + every Admin page for admins (Admin left the sidebar). */}
          <Button variant="ghost" size="icon" asChild title="Settings" aria-label="Settings"
            className={cn(isSettings && 'bg-muted text-foreground')}>
            <Link to={ROUTES.account.info}><Settings className="h-4 w-4"/></Link>
          </Button>

          {userName && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="hidden items-center gap-2 rounded-md px-2 py-1 transition-colors hover:bg-muted sm:inline-flex focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
                >
                  <UserAvatar name={userName} size={26}/>
                  <span className="hidden text-xs text-muted-foreground lg:inline">{userName}</span>
                </button>
              </DropdownMenuTrigger>
              {/* A small user card; everything else lives in Settings. */}
              <DropdownMenuContent align="end" className="w-64 p-0">
                <div className="flex items-center gap-3 px-4 py-3">
                  <UserAvatar name={userName} size={36}/>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-foreground">{userName}</div>
                    {userEmail && userEmail !== userName && <div className="truncate text-xs text-muted-foreground">{userEmail}</div>}
                    <div className="mt-0.5 text-[11px] text-muted-foreground">{isAdmin ? 'Cosmos admin' : 'Cosmos user'}</div>
                  </div>
                </div>
                <div className="border-t border-divider px-4 py-2">
                  <Link to={ROUTES.account.info} className="text-xs text-muted-foreground hover:text-foreground hover:underline">Settings →</Link>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <Button variant="ghost" size="icon" onClick={handleSignOut} title="Sign out" aria-label="Sign out">
            <LogOut className="h-4 w-4"/>
          </Button>
        </div>
      </div>
      <MobileNav open={navOpen} onClose={() => setNavOpen(false)} />
    </header>
  );
}

/** Mobile: the sidebar as a slide-over sheet. */
function MobileNav({ open, onClose }: { open: boolean; onClose: () => void }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true" aria-label="Navigation">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute inset-y-0 left-0 shadow-xl">
        <Sidebar mode="sheet" onNavigate={onClose} />
      </div>
    </div>
  );
}
